import { format, getDay, getDaysInMonth, isToday as dfIsToday, isWeekend, parseISO } from 'date-fns';

/** [year, month] — month is 1-based (1 = January), matching the calendar API. */
export type YearAndMonth = [number, number];

export interface CalendarDayObject {
  /** ISO date, e.g. "2026-06-10" */
  dateString: string;
  dayOfMonth: number;
  isCurrentMonth: boolean;
}

export const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function toDateString(year: number, month: number, day: number): string {
  return format(new Date(year, month - 1, day), 'yyyy-MM-dd');
}

export function createDaysForCurrentMonth(year: number, month: number): CalendarDayObject[] {
  const daysInMonth = getDaysInMonth(new Date(year, month - 1, 1));
  return Array.from({ length: daysInMonth }, (_, index) => ({
    dateString: toDateString(year, month, index + 1),
    dayOfMonth: index + 1,
    isCurrentMonth: true,
  }));
}

export function createDaysForPreviousMonth(year: number, month: number): CalendarDayObject[] {
  // Number of leading cells = weekday of the 1st (Sunday-first grid).
  const firstDayWeekday = getDay(new Date(year, month - 1, 1));
  const previousMonth = new Date(year, month - 2, 1);
  const daysInPreviousMonth = getDaysInMonth(previousMonth);
  const prevYear = previousMonth.getFullYear();
  const prevMonth = previousMonth.getMonth() + 1;

  return Array.from({ length: firstDayWeekday }, (_, index) => {
    const dayOfMonth = daysInPreviousMonth - firstDayWeekday + index + 1;
    return {
      dateString: toDateString(prevYear, prevMonth, dayOfMonth),
      dayOfMonth,
      isCurrentMonth: false,
    };
  });
}

export function createDaysForNextMonth(
  year: number,
  month: number,
  leadingAndCurrentCount: number,
): CalendarDayObject[] {
  // Trailing cells needed to complete the last week row.
  const remainder = leadingAndCurrentCount % 7;
  const trailingCount = remainder === 0 ? 0 : 7 - remainder;
  const nextMonth = new Date(year, month, 1);
  const nextYear = nextMonth.getFullYear();
  const nextMonthNumber = nextMonth.getMonth() + 1;

  return Array.from({ length: trailingCount }, (_, index) => ({
    dateString: toDateString(nextYear, nextMonthNumber, index + 1),
    dayOfMonth: index + 1,
    isCurrentMonth: false,
  }));
}

export function createCalendarGrid(year: number, month: number): CalendarDayObject[] {
  const currentMonthDays = createDaysForCurrentMonth(year, month);
  const previousMonthDays = createDaysForPreviousMonth(year, month);
  const nextMonthDays = createDaysForNextMonth(
    year,
    month,
    previousMonthDays.length + currentMonthDays.length,
  );
  return [...previousMonthDays, ...currentMonthDays, ...nextMonthDays];
}

export function isWeekendDay(dateString: string): boolean {
  return isWeekend(parseISO(dateString));
}

export function isToday(dateString: string): boolean {
  return dfIsToday(parseISO(dateString));
}

export function getMonthDropdownOptions(): { value: number; label: string }[] {
  return Array.from({ length: 12 }, (_, index) => ({
    value: index + 1,
    label: format(new Date(2000, index, 1), 'MMMM'),
  }));
}

export function getYearDropdownOptions(currentYear: number): { value: number; label: string }[] {
  const minYear = currentYear - 4;
  const maxYear = currentYear + 5;
  return Array.from({ length: maxYear - minYear + 1 }, (_, index) => ({
    value: minYear + index,
    label: String(minYear + index),
  }));
}
